import { Component, h, Prop, Watch, Element } from '@stencil/core';

@Component({
  tag: 'signet-scrolling-menu',
  styleUrl: 'signet-scrolling-menu.scss',
})
export class SignetScrollingMenu {
  @Element() el!: HTMLElement;
  @Prop() titles: string[] | string;
  @Prop() sections: string[] | string;
  @Prop() sectionContents: { [key: string]: string } | string;
  @Prop({ mutable: true }) modelValue = 0;

  private _titles: string[];
  private _sections: string[];
  private _sectionContents: { [key: string]: string };

  @Watch('titles')
  parseTitlesProp(newValue: string[] | string) {
    if (typeof newValue === 'string') {
      this._titles = JSON.parse(newValue);
    } else {
      this._titles = newValue;
    }
  }

  @Watch('sections')
  parseSectionsProp(newValue: string[] | string) {
    if (typeof newValue === 'string') {
      this._sections = JSON.parse(newValue);
    } else {
      this._sections = newValue;
    }
  }

  @Watch('sectionContents')
  parseSectionContentsProp(newValue: { [key: string]: string } | string) {
    if (typeof newValue === 'string') {
      this._sectionContents = JSON.parse(newValue);
    } else {
      this._sectionContents = newValue;
    }
  }

  componentWillLoad() {
    this.parseTitlesProp(this.titles);
    this.parseSectionsProp(this.sections);
    this.parseSectionContentsProp(this.sectionContents);
  }

  handleTitleClick(index: number) {
    const titlesContainer = document.querySelector('.titles');
    if (titlesContainer) {
      const titleElements =
        titlesContainer.querySelectorAll<HTMLElement>('.title');

      if (
        titleElements.length > 0 &&
        index >= 0 &&
        index < titleElements.length
      ) {
        // Get the specific title element that was clicked
        const clickedTitle = titleElements[index];

        // Calculate the scroll position to align the clicked title to the far left
        const scrollPosition = clickedTitle.offsetLeft;

        // Set the scrollLeft property of the titlesContainer to scroll to the calculated position
        titlesContainer.scrollLeft = scrollPosition;
      }
    }

    // Scroll to the corresponding section
    const sectionIdentifier = this._sections[index];
    if (sectionIdentifier) {
      const sectionElement = document.getElementById(sectionIdentifier);

      if (sectionElement) {
        const sectionsContainer = document.querySelector('.sections');

        if (sectionsContainer) {
          const targetOffset = sectionElement.offsetTop;

          sectionsContainer.scrollTo({ top: targetOffset, behavior: 'smooth' });
        }
      }
    }

    this.handleMenuClose();

    this.modelValue = index;

    // Vue 2: emit 'input' event
    this.el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: index,
    }));

    // Vue 3: emit 'update:modelValue' event
    this.el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: index,
    }));
  }

  handleScroll() {
    const container = document.querySelector('.sections');
    if (container) {
      const sectionElements = Array.from(container.children);

      sectionElements.forEach((sectionElement: HTMLElement, index) => {
        if (sectionElement) {
          const sectionTop = sectionElement.offsetTop;
          const sectionBottom = sectionTop + sectionElement.clientHeight;

          if (
            container.scrollTop >= sectionTop &&
            container.scrollTop < sectionBottom
          ) {
            this.removeActiveClassFromTitles();
            this.removeActiveClassFromTitlesList();
            this.addActiveClassToTitle(index);
            this.addActiveClassToTitleList(index);

            const titlesContainer = document.querySelector('.titles');

            if (titlesContainer) {
              const titlesWidth = titlesContainer.scrollWidth;
              const visibleWidth = titlesContainer.clientWidth;
              const scrollLeft = titlesContainer.scrollLeft;

              if (scrollLeft > 0) {
                // If scrolled to the far left, hide the left arrow
                const leftArrow =
                  document.querySelector<HTMLElement>('.left-arrow');
                if (leftArrow) {
                  leftArrow.style.display = 'block';
                }
              } else {
                // Show the left arrow if not at the far left
                const leftArrow =
                  document.querySelector<HTMLElement>('.left-arrow');
                if (leftArrow) {
                  leftArrow.style.display = 'none';
                }
              }

              if (scrollLeft + visibleWidth >= titlesWidth) {
                // If scrolled to the far right, hide the right arrow
                const rightArrow =
                  document.querySelector<HTMLElement>('.right-arrow');
                if (rightArrow) {
                  rightArrow.style.display = 'none';
                }
              } else {
                // Show the right arrow if not at the far right
                const rightArrow =
                  document.querySelector<HTMLElement>('.right-arrow');
                if (rightArrow) {
                  rightArrow.style.display = 'block';
                }
              }
            }
          }
        }
      });
    }
  }

  removeActiveClassFromTitles() {
    const titleElements = document.querySelectorAll('.title');
    titleElements.forEach((titleElement) => {
      titleElement.classList.remove('active-title');
    });
  }

  addActiveClassToTitle(index: number) {
    const titleElement = document.querySelector(
      `.title:nth-child(${index + 1})`
    );
    if (titleElement) {
      titleElement.classList.add('active-title');
    }
  }

  removeActiveClassFromTitlesList() {
    const titleElements = document.querySelectorAll('.title-list');
    titleElements.forEach((titleElement) => {
      titleElement.classList.remove('active-title-list');
    });
  }

  addActiveClassToTitleList(index: number) {
    const titleElement = document.querySelector(
      `.title-list:nth-child(${index + 1})`
    );
    if (titleElement) {
      titleElement.classList.add('active-title-list');
    }
  }

  componentDidLoad() {
    this.addActiveClassToTitle(0);
    this.addActiveClassToTitleList(0);

    const container = document.querySelector('.sections');
    if (container) {
      container.addEventListener('scroll', this.handleScroll.bind(this));
    }

    const sectionsContainer = document.querySelector('.sections');
    const titlesContainer = document.querySelector('.titles');

    if (sectionsContainer && titlesContainer) {
      sectionsContainer.addEventListener('scroll', () => {
        const activeTitleIndex = Array.from(
          sectionsContainer.children
        ).findIndex((sectionElement: HTMLElement) => {
          if (sectionElement) {
            const sectionTop = sectionElement.offsetTop;
            const sectionBottom = sectionTop + sectionElement.clientHeight;
            return (
              sectionsContainer.scrollTop >= sectionTop &&
              sectionsContainer.scrollTop < sectionBottom
            );
          }
        });

        if (activeTitleIndex !== -1) {
          const activeTitleElement = titlesContainer.children[
            activeTitleIndex
          ] as HTMLElement;
          const scrollPosition = activeTitleElement.offsetLeft;
          titlesContainer.scrollLeft = scrollPosition;

          this.modelValue = activeTitleIndex;

          // Vue 2: emit 'input' event
          this.el.dispatchEvent(new CustomEvent('input', {
            bubbles: true,
            composed: true,
            detail: activeTitleIndex,
          }));

          // Vue 3: emit 'update:modelValue' event
          this.el.dispatchEvent(new CustomEvent('update:modelValue', {
            bubbles: true,
            composed: true,
            detail: activeTitleIndex,
          }));
        }
      });
    }

    // Add a scroll event listener to the titlesContainer
    if (titlesContainer) {
      titlesContainer.addEventListener(
        'scroll',
        this.handleTitlesContainerScroll
      );
    }
  }

  handleTitlesContainerScroll() {
    const titlesContainer = document.querySelector('.titles');

    if (titlesContainer) {
      const titlesWidth = titlesContainer.scrollWidth;
      const visibleWidth = titlesContainer.clientWidth;
      const scrollLeft = titlesContainer.scrollLeft;

      if (scrollLeft > 0) {
        // If scrolled to the far left, hide the left arrow
        const leftArrow = document.querySelector<HTMLElement>('.left-arrow');
        if (leftArrow) {
          leftArrow.style.display = 'block';
        }
      } else {
        // Show the left arrow if not at the far left
        const leftArrow = document.querySelector<HTMLElement>('.left-arrow');
        if (leftArrow) {
          leftArrow.style.display = 'none';
        }
      }

      if (scrollLeft + visibleWidth >= titlesWidth) {
        // If scrolled to the far right, hide the right arrow
        const rightArrow = document.querySelector<HTMLElement>('.right-arrow');
        if (rightArrow) {
          rightArrow.style.display = 'none';
        }
      } else {
        // Show the right arrow if not at the far right
        const rightArrow = document.querySelector<HTMLElement>('.right-arrow');
        if (rightArrow) {
          rightArrow.style.display = 'block';
        }
      }
    }
  }

  handleRightArrowClick() {
    const container = document.querySelector('.titles');

    if (container) {
      const titleWidth =
        container.querySelector<HTMLElement>('.title').offsetWidth;
      container.scrollLeft += titleWidth;
    }

    const rightArrow = document.querySelector<HTMLElement>('.right-arrow');
    if (rightArrow) {
      rightArrow.style.display = 'none';
    }

    const leftArrow = document.querySelector<HTMLElement>('.left-arrow');
    if (leftArrow) {
      leftArrow.style.display = 'block';
    }
  }

  handleLeftArrowClick() {
    const container = document.querySelector('.titles');

    if (container) {
      const titleWidth =
        container.querySelector<HTMLElement>('.title').offsetWidth;
      container.scrollLeft -= titleWidth;
    }

    const leftArrow = document.querySelector<HTMLElement>('.left-arrow');
    if (leftArrow) {
      leftArrow.style.display = 'none';
    }

    const rightArrow = document.querySelector<HTMLElement>('.right-arrow');
    if (rightArrow) {
      rightArrow.style.display = 'block';
    }
  }

  handleMenuOpen() {
    const titleListContainer = document.querySelector<HTMLElement>(
      '.titles-list-container'
    );

    if (titleListContainer) {
      titleListContainer.style.display = 'block';
    }
  }

  handleMenuClose() {
    const titleListContainer = document.querySelector<HTMLElement>(
      '.titles-list-container'
    );

    if (titleListContainer) {
      titleListContainer.style.display = 'none';
    }
  }

  render() {
    return (
      <div class="signet-scrolling-menu">
        <div class="header">
          <div class="icon menu-icon">
            <signet-icon
              icon="format_list_bulleted"
              onClick={() => this.handleMenuOpen()}
            />
          </div>
          <div class="titles-list-container" style={{ display: 'none' }}>
            {this._titles.map((title, index) => (
              <div
                class="title-list"
                key={index}
                onClick={() => this.handleTitleClick(index)}
              >
                {title}
              </div>
            ))}

            <div class="icon close-icon">
              <signet-icon
                icon="close"
                onClick={() => this.handleMenuClose()}
              />
            </div>
          </div>
          <div class="icon left-arrow" style={{ display: 'none' }}>
            <signet-icon
              icon="keyboard_arrow_left"
              onClick={() => this.handleLeftArrowClick()}
            />
          </div>
          <div class="titles">
            {this._titles.map((title, index) => (
              <div
                class="title"
                key={index}
                onClick={() => this.handleTitleClick(index)}
              >
                {title}
              </div>
            ))}
          </div>
          <div class="icon right-arrow" style={{ display: 'none' }}>
            <signet-icon
              icon="keyboard_arrow_right"
              onClick={() => this.handleRightArrowClick()}
            />
          </div>
        </div>
        <div class="sections">
          {this._sections.map((section, index) => (
            <div
              class="section"
              data-section={section}
              id={section}
              key={index}
            >
              <div innerHTML={this._sectionContents[section]}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }
}
